import React, { useEffect, useMemo, useState } from 'react';
import { SEO } from '../components/SEO';
import { Container } from '../components/ui/Container';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { HelpArticleCard } from '../components/HelpArticleCard';
import { helpArticles, HelpArticle } from '../data/helpArticles';
import { Search, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { getTotalViews } from '../utils/viewCounts';
const ARTICLES_PER_PAGE = 9;
const CATEGORIES = ['All', 'Getting Started', 'Integrations', 'Troubleshooting', 'Best Practices', 'Billing'];
export function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [, forceUpdate] = useState({});
  // Force re-render to update view counts from localStorage
  useEffect(() => {
    // Update view counts every time the page is visited
    forceUpdate({});
  }, []);
  // Enhance articles with dynamic view counts
  const articlesWithDynamicViews = useMemo(() => {
    return helpArticles.map(article => ({
      ...article,
      views: getTotalViews(article.id, article.views)
    }));
  }, [helpArticles, forceUpdate]);
  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    return articlesWithDynamicViews.filter(article => {
      const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
      const matchesSearch = searchQuery === '' || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || article.content.toLowerCase().includes(searchQuery.toLowerCase()) || article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory, articlesWithDynamicViews]);
  // Pagination logic
  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = filteredArticles.slice((currentPage - 1) * ARTICLES_PER_PAGE, currentPage * ARTICLES_PER_PAGE);
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    name: 'Flowsense Help Center',
    description: 'Comprehensive help articles and guides for Flowsense users',
    mainEntity: helpArticles.slice(0, 10).map(article => ({
      '@type': 'Question',
      name: article.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: article.excerpt
      }
    }))
  };
  return <>
      <SEO title="Help Center - Flowsense" description="Find answers, troubleshooting guides, and best practices for Flowsense. Learn how to optimize your Klaviyo flows with our comprehensive help articles." canonical="https://www.useflowsense.com/help" structuredData={structuredData} data-id="element-1779" />
      <div className="bg-gray-50 min-h-screen" data-id="element-1780">
        {/* Hero Section */}
        <div className="bg-gray-900 py-20 lg:py-28 text-center text-white relative overflow-hidden" data-id="element-1781">
          <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/dPM9KUkaUrxRQPXFhc49CA/grid-pattern.svg')] opacity-10" data-id="element-1782"></div>
          <Container className="relative z-10" data-id="element-1783">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-sm rounded-xl mb-6 border border-white/20" data-id="element-1784">
              <HelpCircle className="h-8 w-8 text-indigo-400" data-id="element-1785" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6" data-id="element-1786">
              Help & Support
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10" data-id="element-1787">
              Find answers, troubleshooting guides, and best practices to help
              you get the most out of Flowsense.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative" data-id="element-1788">
              <div className="relative" data-id="element-1789">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1790">
                  <Search className="h-5 w-5 text-gray-400" data-id="element-1791" />
                </div>
                <Input type="text" placeholder="Search for articles, topics, or keywords..." className="w-full pl-11 py-6 text-lg rounded-xl border-0 shadow-xl text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-indigo-500" value={searchQuery} onChange={handleSearchChange} data-id="element-1792" />
              </div>
            </div>
          </Container>
        </div>

        <Container className="py-12" data-id="element-1793">
          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-1794">
            {CATEGORIES.map(category => <button key={category} onClick={() => handleCategoryChange(category)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${selectedCategory === category ? 'bg-indigo-600 text-white shadow-md transform scale-105' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'}`} data-id="element-1795">
                {category}
              </button>)}
          </div>

          {/* Results Info */}
          <div className="mb-8 text-gray-500 text-sm font-medium" data-id="element-1796">
            Showing {filteredArticles.length} article
            {filteredArticles.length !== 1 ? 's' : ''}
            {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </div>

          {/* Articles Grid */}
          {paginatedArticles.length > 0 ? <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-1797">
              {paginatedArticles.map(article => <HelpArticleCard key={article.id} article={article} data-id="element-1798" />)}
            </div> : <div className="text-center py-20 bg-white rounded-2xl border border-gray-200 shadow-sm mb-16" data-id="element-1799">
              <div className="inline-flex items-center justify-center p-4 bg-gray-100 rounded-full mb-4" data-id="element-1800">
                <Search className="h-8 w-8 text-gray-400" data-id="element-1801" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1802">
                No articles found
              </h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6" data-id="element-1803">
                We couldn't find any articles matching your search. Try using
                different keywords or browsing by category.
              </p>
              <Button variant="outline" onClick={() => {
            setSearchQuery('');
            setSelectedCategory('All');
          }} data-id="element-1804">
                Clear Filters
              </Button>
            </div>}

          {/* Pagination */}
          {totalPages > 1 && <div className="flex justify-center items-center space-x-2" data-id="element-1805">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="w-10 h-10 p-0 flex items-center justify-center" data-id="element-1806">
                <ChevronLeft className="h-4 w-4" data-id="element-1807" />
              </Button>

              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => handlePageChange(page)} className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} data-id="element-1808">
                  {page}
                </button>)}

              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-10 h-10 p-0 flex items-center justify-center" data-id="element-1809">
                <ChevronRight className="h-4 w-4" data-id="element-1810" />
              </Button>
            </div>}
        </Container>
      </div>
    </>;
}